/* lib/analyticsAccess.js — реестр доступов команды к аналитике iiko/Syrve.
 * ЗЕРКАЛО backend/integrations/access_catalog.py: ключи и русские лейблы
 * совпадают байт-в-байт (конвенция inventoryCriteria/abcMeta/foodcostMeta).
 *
 * Ключ: `analytics.<tab>` (вкладка) и `analytics.<tab>.<section>` (секция).
 * Карта эффективных прав текущего юзера приходит в GET /api/integrations/
 * полем `access` ({key: bool}); отсутствие карты или ключа = разрешено
 * (дефолт «всё открыто»). Запрет вкладки закрывает все её секции. */

export const ACCESS_PREFIX = 'analytics';

// Вкладки-отчёты (порядок = порядок вкладок в UI).
export const ACCESS_TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'pnl', label: 'ОПиУ' },
  { key: 'abc', label: 'ABC-анализ блюд' },
  { key: 'liquidity', label: 'Ликвидность' },
  { key: 'categories', label: 'Продажи' },
  { key: 'purchases', label: 'Закупки' },
  { key: 'movement', label: 'Движение товара' },
  { key: 'inventory', label: 'Инвентаризация' },
  // «Персонал» (бывш. «Официанты», staff-groups): вкладка делится по ролям из кассы.
  // Ключ `staff` и права прежние — переименована только подпись.
  { key: 'staff', label: 'Персонал' },
  { key: 'settings', label: 'Настройки' },
];

// Секции внутри вкладок (для матрицы ролей и гейтов рендера).
export const ACCESS_SECTIONS = {
  overview: [
    { key: 'today', label: 'Сегодня и вчера' },
    { key: 'plan', label: 'План выручки' },
    { key: 'income', label: 'Доходы' },
    { key: 'dynamics', label: 'Динамика выручки' },
    { key: 'guests', label: 'Гости, средний чек, заказы' },
    { key: 'performance', label: 'Производительность' },
    { key: 'tables', label: 'Столы: спрос и загрузка' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  pnl: [
    { key: 'summary', label: 'Сводка месяца' },
    { key: 'tree', label: 'Таблица ОПиУ' },
    { key: 'expenses_edit', label: 'Ручные расходы (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  abc: [
    { key: 'manage', label: 'Пороги и исключения (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  liquidity: [
    { key: 'window_edit', label: 'Окно расхода (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  categories: [
    { key: 'charts', label: 'Графики (структура, динамика, рост)' },
    { key: 'table', label: 'Таблица и карточка разреза' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  purchases: [
    { key: 'charts', label: 'Графики (структура, динамика)' },
    { key: 'suppliers', label: 'Поставщики' },
    { key: 'products', label: 'Топ товаров' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  movement: [
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  inventory: [
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  staff: [
    { key: 'leakage', label: 'Потери и удаления (скидки, возвраты, категории удалений, причины)' },
    { key: 'waiter_detail', label: 'Карточка официанта' },
    { key: 'manage', label: 'Скрытые сотрудники (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  settings: [],
};

export function accessKey(tab, section) {
  return ACCESS_PREFIX + '.' + tab + (section ? '.' + section : '');
}

// Доступна ли вкладка. access — карта {key: bool} из GET /api/integrations/
// (может отсутствовать: personal ws / старый бэк → всё разрешено).
export function canTab(access, tab) {
  if (!access) return true;
  return access[accessKey(tab)] !== false;
}

// Доступна ли секция: закрытая вкладка закрывает и все её секции.
export function canSection(access, tab, section) {
  if (!access) return true;
  if (!canTab(access, tab)) return false;
  return access[accessKey(tab, section)] !== false;
}

// Полный список ключей каталога (для матриц ролей/исключений).
export function allAccessKeys() {
  const keys = ACCESS_TABS.map((t) => accessKey(t.key));
  for (const t of ACCESS_TABS) {
    for (const s of (ACCESS_SECTIONS[t.key] || [])) keys.push(accessKey(t.key, s.key));
  }
  return keys;
}
